<template>
    <v-card
        class="elevation-1 round-card background-1 mr-4"
        :class="{ 'ml-6': maillog.direction === 'outbound' }"
    >
        <!-- Header sempre visibile - cliccabile per toggle -->
        <v-card-title
            class="pa-3 pb-1 cursor-pointer"
            @click="$emit('toggle')"
        >
            <div class="d-flex align-center flex-wrap" style="width: 100%">
                <!-- Per outbound con user -->
                <template v-if="maillog.direction === 'outbound' && maillog.user">

                    <v-avatar
                        size="32"
                        class="primary mr-2"
                    >
                        <span class="white--text fs14">{{ getInitial }}</span>
                    </v-avatar>
                </template>
                <!-- Per inbound o senza user -->
                <v-avatar
                    v-else
                    :color="maillog.direction === 'outbound' ? 'primary' : 'grey'"
                    size="32"
                    class="mr-2"
                >
                    <v-icon dark small>{{ maillog.direction === 'outbound' ? 'mdi-email-fast-outline' : 'mdi-email-arrow-left-outline' }}</v-icon>
                </v-avatar>
                <div class="flex-grow-1">
                    <div class="subtitle-1 font-weight-medium" style="line-height: 1">
                        {{ maillog.direction === 'outbound' ? maillog.from_name : maillog.from }}
                    </div>
                    <div class="caption grey--text">
                        {{ maillog.from }}
                    </div>
                </div>
                <div class="caption grey--text text-right d-flex align-center">
                    <v-chip x-small class="mr-2" :color="maillog.direction === 'outbound' ? 'primary' : 'grey'" dark>
                        {{ maillog.direction === 'outbound' ? 'Inviata' : 'Ricevuta' }}
                    </v-chip>
                    {{ maillog.send_date | datetime }}
                    <v-icon small class="ml-2">
                        {{ collapsed ? 'mdi-chevron-down' : 'mdi-chevron-up' }}
                    </v-icon>
                </div>
            </div>
        </v-card-title>

        <!-- Preview quando collassato -->
        <v-card-text
            v-if="collapsed"
            class="pa-3 pt-0 grey--text text-truncate cursor-pointer"
            @click="$emit('toggle')"
        >
            {{ getPreview }}
        </v-card-text>

        <!-- Contenuto completo quando espanso -->
        <template v-else>
            <v-card-subtitle class="px-3 py-1 pt-4" v-if="maillog.to && maillog.to.length">
                <span class="grey--text">A: </span>
                <span>{{ formatRecipients(maillog.to) }}</span>
            </v-card-subtitle>

            <v-card-subtitle class="px-3 py-1" v-if="maillog.cc && maillog.cc.length">
                <span class="grey--text">CC: </span>
                <span>{{ formatRecipients(maillog.cc) }}</span>
            </v-card-subtitle>

            <v-divider></v-divider>

            <v-card-text class="pa-3">
                <div
                    v-if="isHtml(maillog.body)"
                    v-html="sanitizeHtml(maillog.body)"
                    v-inline-files
                    class="email-body"
                ></div>
                <div v-else class="email-body">
                    {{ maillog.body }}
                </div>
            </v-card-text>

            <!-- Attachments -->
            <v-card-text v-if="maillog.attachments && maillog.attachments.length" class="pa-3 pt-0">
                <v-divider class="mb-3"></v-divider>
                <div class="subtitle-2 grey--text mb-2">
                    <v-icon small class="mr-1">mdi-paperclip</v-icon>
                    Allegati ({{ maillog.attachments.length }})
                </div>
                <v-chip
                    v-for="attachment in maillog.attachments"
                    :key="attachment.id"
                    small
                    class="mr-2 mb-2"
                    @click="handleAttachmentClick(attachment)"
                >
                    <v-icon left small>{{ getFileIcon(attachment.ext) }}</v-icon>
                    {{ attachment.origin_file_name || attachment.name }}
                </v-chip>
            </v-card-text>
        </template>

        <file-preview-dialog
            v-model="previewDialog"
            :doc="previewDoc"
            :url="previewUrl"
        />
    </v-card>
</template>

<script>
import FilePreviewDialog from '@/components/common/FilePreviewDialog'
import { sanitizeHtml } from '@/utils/sanitizeHtml'

export default {
    name: 'MaillogTimelineItem',

    components: { FilePreviewDialog },

    props: {
        maillog: {
            type: Object,
            required: true
        },
        collapsed: {
            type: Boolean,
            default: false
        }
    },

    data() {
        return {
            previewDialog: false,
            previewDoc: null,
            previewUrl: null
        }
    },

    computed: {
        getInitial() {
            if (this.maillog.from_name) {
                return this.maillog.from_name.charAt(0).toUpperCase()
            }
            return 'U'
        },

        getPreview() {
            if (!this.maillog.body) return ''
            const text = this.maillog.body.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim()
            return text.length > 150 ? text.substring(0, 150) + '...' : text
        }
    },

    methods: {
        formatRecipients(recipients) {
            if (!recipients) return ''
            if (Array.isArray(recipients)) {
                return recipients.map(r => r.email || r).join(', ')
            }
            return recipients
        },

        sanitizeHtml,

        isHtml(content) {
            if (!content) return false
            return /<[a-z][\s\S]*>/i.test(content)
        },

        getFileIcon(ext) {
            const icons = {
                pdf: 'mdi-file-pdf-box',
                doc: 'mdi-file-word',
                docx: 'mdi-file-word',
                xls: 'mdi-file-excel',
                xlsx: 'mdi-file-excel',
                ppt: 'mdi-file-powerpoint',
                pptx: 'mdi-file-powerpoint',
                jpg: 'mdi-file-image',
                jpeg: 'mdi-file-image',
                png: 'mdi-file-image',
                gif: 'mdi-file-image',
                zip: 'mdi-folder-zip',
                rar: 'mdi-folder-zip',
                txt: 'mdi-file-document',
                csv: 'mdi-file-delimited'
            }
            return icons[ext?.toLowerCase()] || 'mdi-file'
        },

        isPreviewable(attachment) {
            var exts = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'svg', 'mp4', 'webm', 'pdf']
            return exts.includes((attachment.ext || '').toLowerCase())
        },

        handleAttachmentClick(attachment) {
            if (attachment.path && this.isPreviewable(attachment)) {
                this.previewDoc = { name: attachment.origin_file_name || attachment.name, ext: attachment.ext }
                this.previewUrl = '/files/' + attachment.path
                this.previewDialog = true
            } else {
                this.downloadAttachment(attachment)
            }
        },

        downloadAttachment(attachment) {
            if (attachment.path) {
                this.$eventBusUtility.getFile(this.$store, '/files/' + attachment.path, attachment.origin_file_name || attachment.name)
            }
        }
    }
}
</script>

<style scoped>
.custom-avatar {
    background-size: cover;
    background-position: center;
}

.email-body {
    max-height: 500px;
    overflow-y: auto;
}

/* Tema scuro: la mail e' un documento del mittente e arriva con i colori scritti
   inline (Outlook forza color: rgb(0,0,0) su ogni paragrafo), che il sanitizer
   conserva per non perdere dimensioni e layout. Sul fondo scuro della card il
   testo diventava illeggibile: il body si mostra su una "carta" chiara, come
   fanno Gmail e Outlook, senza toccare i colori scelti dal mittente. */
.theme--dark .email-body {
    background-color: #fafafa;
    color: rgba(0, 0, 0, 0.87);
    padding: 12px;
    border-radius: 4px;
}

.email-body >>> img {
    max-width: 100%;
    height: auto;
}

.email-body >>> a {
    color: #1976d2;
}

.email-body >>> blockquote {
    border-left: 3px solid #ccc;
    padding-left: 12px;
    margin-left: 0;
    color: #666;
}
</style>
